import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import fetchWrapper from '../utils/httpClient';
import {
  API_GET_ACCOUNT_ROLES,
  API_GET_ROLE_GRADE,
  API_CREATE_ROLE,
  API_DELETE_ROLE,
  API_UPDATE_ROLE
} from '../utils/apis';

// Fetch roles list
export const fetchRoles = createAsyncThunk('roles/fetchRoles', async (url = `${API_GET_ACCOUNT_ROLES}?page=0&size=10`) => {
  try {
    const response = await fetchWrapper.get(url);
    return response.resdata; // Now expecting a paginated response
  } catch (error) {
    throw new Error(error.message);
  }
});

// Fetch role grades
export const fetchRoleGrades = createAsyncThunk('roles/fetchRoleGrades', async () => {
  try {
    const response = await fetchWrapper.get(API_GET_ROLE_GRADE);
    return response.resdata;
  } catch (error) {
    throw new Error(error.message);
  }
});

// Create new role
export const createRole = createAsyncThunk('roles/createRole', async (payload, { rejectWithValue }) => {
  try {
    const response = await fetchWrapper.post(API_CREATE_ROLE, payload);
    return response;
  } catch (error) {
    return rejectWithValue(error);
  }
});

// Update existing role
export const updateRole = createAsyncThunk('roles/updateRole', async (payload) => {
  try {
    const response = await fetchWrapper.put(API_UPDATE_ROLE, payload);
    return response;
  } catch (error) {
    throw new Error(error.message);
  }
});

// Delete role
export const deleteRole = createAsyncThunk('roles/deleteRole', async (roleId) => {
  try {
    const response = await fetchWrapper.delete(`${API_DELETE_ROLE}${roleId}`);
    return response;
  } catch (error) {
    throw new Error(error.message);
  }
});

// Fetch role details
export const fetchRoleDetails = createAsyncThunk('roles/fetchRoleDetails', async (roleId) => {
  try {
    const url = `/cms/api/accountRoles/roleId/${roleId}`;
    const response = await fetchWrapper.get(url);
    return response.resdata;
  } catch (error) {
    throw new Error(error.message);
  }
});

const roleSlice = createSlice({
  name: 'roles',
  initialState: {
    data: {
      content: [],
      totalElements: 0,
      totalPages: 0,
      number: 0,
      size: 10
    },
    grades: [],
    loading: false,
    error: null,
    roleDetails: null,
    status: null
  },
  extraReducers: (builder) => {
    builder
      // Fetch roles handlers
      .addCase(fetchRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = "pending";
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.status = "succeeded";
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.status = "failed";
      })

      // Fetch role grades handlers
      .addCase(fetchRoleGrades.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = "pending";
      })
      .addCase(fetchRoleGrades.fulfilled, (state, action) => {
        state.loading = false;
        state.grades = action.payload;
        state.status = "succeeded";
      })
      .addCase(fetchRoleGrades.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.status = "failed";
      })

      // Create role handlers
      .addCase(createRole.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = "pending";
      })
      .addCase(createRole.fulfilled, (state) => {
        state.loading = false;
        state.status = "succeeded";
      })
      .addCase(createRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
        state.status = "failed";
      })

      // Update role handlers
      .addCase(updateRole.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = "pending";
      })
      .addCase(updateRole.fulfilled, (state) => {
        state.loading = false;
        state.status = "succeeded";
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.status = "failed";
      })

      // Delete role handlers
      .addCase(deleteRole.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = "pending";
      })
      .addCase(deleteRole.fulfilled, (state) => {
        state.loading = false;
        state.status = "succeeded";
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.status = "failed";
      })

      // Fetch role details handlers
      .addCase(fetchRoleDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = "pending";
      })
      .addCase(fetchRoleDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.roleDetails = action.payload;
        state.status = "succeeded";
      })
      .addCase(fetchRoleDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.status = "failed";
      });
  }
});

export default roleSlice.reducer;